import { useStore } from '../js/store/index.js'
import { isSuccessResponse, triggerAlert } from './store/util.js'
import { post, get } from './api/api.js'

class Auth {
  constructor() {
    this.store = useStore()
    const token = window.localStorage.getItem('token')

    if (!token) {
      this.clearUserSession()
    } else {
      this.token = token
      this.gate = this.getItem('gate')
      axios.defaults.headers.common.Authorization = `Bearer ${this.token}`
    }
  }

  /**
   * Parses JSON stringified storage items to actual JS types (boolean, object/null, etc.).
   * Note: this is not used for token handling, which is always just a string.
   *
   * @param {*} item stringified value
   * @returns parsed JS value (boolean, null, object, string)
   */
  getItem(item) {
    return JSON.parse(window.localStorage.getItem(item))
  }

  /**
   * Verify logged-in user credentials and set in local storage.
   *
   * @param {object} user
   */
  async setUser(user) {
    this.user = {
      id: user.id,
      unhashed_id: user.unhashed_id,
      name: user.name,
      email: user.email,
      admin_user: user.admin_user || false,
      super_admin_user: user.super_admin_user || false,
      cate_regional_admin_user: user.cate_regional_admin_user || false,
      is_contributor_admin_connections_only:
        user.is_contributor_admin_connections_only || false,
      tenant_uuid: user.tenant_uuid,
      organisations: user.organisations || [],
    }

    this.gate = this.user.admin_user

    window.localStorage.setItem('user', JSON.stringify(this.user))
    window.localStorage.setItem('gate', JSON.stringify(this.gate))

    this.store.dispatch('shared/dispatchSetUserSettings', {
      user: this.user,
      userAccess: this.gate,
    })

    if (user.organisations?.length) {
      // This user must be a contributor - update their organisations and selected organisations
      await this.store.dispatch(
        'contributors/dispatchUpdateOrganisations',
        user.organisations,
      )
    }
  }

  /**
   * Locally store logged-in user credentials (token and expiry);
   * attach token to axios http headers
   *
   * @param {object} token
   * @param {object} user
   * @param {*} expires
   */
  async signIn(token, user, expires) {
    window.localStorage.setItem('token', token)
    window.localStorage.setItem('expires', JSON.stringify(expires))
    axios.defaults.headers.common.Authorization = `Bearer ${token}`

    this.token = token
    this.expires = user
    await this.setUser(user)
  }

  /**
   * Sign out logged-in user (by clearing session).
   */
  async signOut() {
    const client = this.store.getters['shared/getClient']
    const isContributor = client === 'client1contributor'
    const endpoint = isContributor
      ? '/central-api/logout'
      : '/api/auth/user/signout'

    const response = await post(endpoint)

    if (isSuccessResponse(response)) {
      this.clearUserSession()
    }

    if (!isContributor) {
      triggerAlert(response)
    }
  }

  /**
   * Clear all session user auth credentials.
   * Revoke token and user data (reset to falsy values).
   * Note: token must first be revoked on server side!
   */
  clearUserSession() {
    this.token = ''
    this.user = null
    this.expires = null
    this.gate = false

    window.localStorage.setItem('token', this.token)
    window.localStorage.setItem('user', JSON.stringify(this.user))
    window.localStorage.setItem('expires', JSON.stringify(this.expires))
    window.localStorage.setItem('gate', JSON.stringify(this.gate))
    axios.defaults.headers.common.Authorization = false

    // clear user from store
    this.store.dispatch('shared/dispatchSetUserSettings')
  }

  /**
   * Refresh logged-in user credentials and update storage,
   * e.g., on refresh, if permissions have changed.
   * Note: client is set on every router change.
   */
  async updateUser(route) {
    const client = this.store.getters['shared/getClient']
    if (client) {
      let user = null

      if (client === 'client1contributor') {
        if (!this.store.getters['contributors/isPublicProfileContext'](route)) {
          // Contributor portal context - check for auth'd user
          // Contriubtor public profile context (same client!) - DON'T get user!
          const response = await get('/central-api/user')
          if (isSuccessResponse(response)) {
            user = response.data.user
          }
        }
      } else if (client === 'client1' || client === 'client2') {
        // Central context - don't get user!
        return
      } else {
        // Tenant context - check for auth'd user
        const response = await get('/api/auth/user')
        if (isSuccessResponse(response)) {
          user = response.data.user
        }
      }

      if (user) {
        window.localStorage.setItem('expires', JSON.stringify(this.expires))
        await this.setUser(user)
      }
    }
  }

  /**
   * Get logged-in user details.
   *
   * @returns {object} logged-in user
   */
  getUser() {
    return this.user
  }

  /**
   * Admin validation. Is (logged-in) user an admin?
   *
   * @returns {boolean} is user an admin
   */
  isAdmin() {
    return !!this.gate
  }

  /**
   * Authentication validation. Is user logged in?
   *
   * @returns {boolean} is user logged in
   */
  isAuthenticated() {
    return !!this.token
  }
}

export default Auth
